import {clientSettings, featureFlags, timezones, users} from '@cl/shared';
import {createAsyncThunk} from '@reduxjs/toolkit';
import * as authService from 'common/src/modules/auth/auth.service';
import enumsService from 'common/src/modules/enums/enums.service';
import {CLEnumType} from 'common/src/modules/enums/enums.types';
import clientSettingsApi from 'common/src/modules/clientSettings/clientSettings.api';
import featureFlagsApi from 'common/src/modules/featureFlags/featureFlags.api';
import initializeAiContent from 'common/src/modules/shared/aiContent/utils/initializeAiContent';
import initializeAnalytics from 'common/src/modules/shared/analytics/utils/initializeAnalytics';
import initializeBrandGuidelines from 'common/src/modules/shared/brandGuidelines/utils/initializeBrandGuidelines';
import initializeCampaigns from 'common/src/modules/shared/campaigns/utils/initializeCampaigns';
import initializeCopilot from 'common/src/modules/shared/copilot/utils/initializeCopilot';
import initializeCopilotChat from 'common/src/modules/shared/copilot/utils/initializeCopilotChat';
import initializeCreativeTemplates from 'common/src/modules/shared/creativeTemplates/utils/initializeCreativeTemplates';
import initializeCreatives from 'common/src/modules/shared/creatives/utils/initializeCreatives';
import timezonesService from 'common/src/modules/timezones/timezones.service';
import usersApi from 'common/src/modules/users/users.api';
import {setAppInitialized} from '../application.slice';
import {CLDispatch, CLStore, RootState} from '../store';

const initializeApp = createAsyncThunk<void, void, {dispatch: CLDispatch; state: RootState}>(
  'application/initializeApp',
  async (_, store: CLStore) => {
    try {
      await _initializeFeatureFlags();
      await _initializeClientSettings();
      await _initializeTimezones();
      await _initializeEnums();
      await _initializeUsers();

      await Promise.all([
        initializeCopilot(store),
        _initializeCampaigns(store),
        initializeCreativeTemplates(store),
        initializeCreatives(store),
        initializeBrandGuidelines(store),
        initializeAnalytics(store),
        initializeAiContent(store),
      ]);

      // must initialize the one-and-only copilot chat only after ALL agents have been registered
      initializeCopilotChat(store);
    } catch (error) {
      console.error(error);
    } finally {
      store.dispatch(setAppInitialized(true));
    }
  },
);

/**
 * Initialize enums
 * @description This function initializes enums for the application.
 * It is used to avoid loading enums lazily.
 */
async function _initializeEnums() {
  if (!authService.isLoggedIn()) {
    return;
  }

  await Promise.all([enumsService.loadEnums(CLEnumType.CampaignPage)]);
}

async function _initializeFeatureFlags() {
  const isLoggedIn = authService.isLoggedIn();

  try {
    const enabledFlags = isLoggedIn ? await featureFlagsApi.getEnabledFlags() : new Set([]);

    featureFlags.enabledFlagsSubject.data = enabledFlags;
  } catch (error) {
    featureFlags.enabledFlagsSubject.data = new Set([]);
  }
}

async function _initializeClientSettings() {
  const isLoggedIn = authService.isLoggedIn();

  try {
    const settingsMap = isLoggedIn
      ? await clientSettingsApi.getClientSettingsValuesMap()
      : new Map<string, string>();

    clientSettings.clientSettingsValuesSubject.data = settingsMap;
  } catch (error) {
    clientSettings.clientSettingsValuesSubject.data = new Map<string, string>();
  }
}

async function _initializeTimezones() {
  try {
    if (!authService.isLoggedIn()) {
      return;
    }

    const info = await timezonesService.getDateTimeInfo();
    timezones.dateTimeInfo.current = info;
    timezones.dateTimeInfoSubject.data = info;
  } catch (error) {
    timezones.dateTimeInfo.current = null;
    timezones.dateTimeInfoSubject.data = null;
  }
}

async function _initializeUsers() {
  if (!authService.isLoggedIn()) {
    return;
  }

  try {
    const user = await usersApi.get();

    if (user) {
      users.userSubject.data = user;
    }
  } catch (error) {
    console.error('Failed to initialize users:', error);
  }
}

async function _initializeCampaigns(store: CLStore) {
  if (!authService.isLoggedIn()) {
    return;
  }

  await initializeCampaigns(store);
}

export default initializeApp;
